import React, { useEffect, useRef, useState } from "react";
import { Edit, ZoomIn, ZoomOut, RotateCw, Info } from "lucide-react";
import { RestaurantTable, Order, Staff } from "../types";
import { supabase } from "../lib/supabase";

interface FloorplanItem {
  id: string;
  type: "table" | "bar" | "kitchen" | "entrance" | "till" | "wall" | "counter";
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  shape?: "circle" | "square" | "rectangle";
  table?: RestaurantTable;
  label?: string;
  color?: string;
}

interface FloorplanViewProps {
  currentStaff: Staff;
  tables: RestaurantTable[];
  orders: Order[];
  onTableSelect: (table: RestaurantTable) => void;
  onEditFloorplan: () => void;
  onTableStatusUpdate: (tableId: string, status: string) => void;
}

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function badgeForStatus(status: string) {
  switch (status) {
    case "available":
      return "bg-emerald-50 text-emerald-800 ring-emerald-200";
    case "occupied":
      return "bg-rose-50 text-rose-800 ring-rose-200";
    case "reserved":
      return "bg-amber-50 text-amber-900 ring-amber-200";
    case "cleaning":
      return "bg-slate-100 text-slate-700 ring-slate-200";
    default:
      return "bg-slate-100 text-slate-700 ring-slate-200";
  }
}

function dotForStatus(status: string) {
  switch (status) {
    case "available":
      return "bg-emerald-500";
    case "occupied":
      return "bg-rose-500";
    case "reserved":
      return "bg-amber-500";
    case "cleaning":
      return "bg-slate-500";
    default:
      return "bg-slate-500";
  }
}

const FloorplanView: React.FC<FloorplanViewProps> = ({
  currentStaff,
  tables,
  orders,
  onTableSelect,
  onEditFloorplan,
  onTableStatusUpdate,
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);

  const [floorplanItems, setFloorplanItems] = useState<FloorplanItem[]>([]);
  const [zoom, setZoom] = useState<number>(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    loadFloorplan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables, orders]);

  useEffect(() => {
    if (floorplanItems.length > 0 && !isInitialized) {
      autoFitFloorplan();
      setIsInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorplanItems, isInitialized]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setZoom((prev) => Math.max(0.3, Math.min(3, prev * delta)));
      }
    };

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener("wheel", handleWheel, { passive: false });
      return () => canvas.removeEventListener("wheel", handleWheel);
    }
  }, []);

  const autoFitFloorplan = () => {
    if (!canvasRef.current || floorplanItems.length === 0) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const containerWidth = Math.max(1, rect.width);
    const containerHeight = Math.max(1, rect.height);

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    floorplanItems.forEach((item) => {
      minX = Math.min(minX, item.x);
      minY = Math.min(minY, item.y);
      maxX = Math.max(maxX, item.x + item.width);
      maxY = Math.max(maxY, item.y + item.height);
    });

    const padding = 120;
    const contentWidth = maxX - minX + padding * 2;
    const contentHeight = maxY - minY + padding * 2;

    const zoomX = containerWidth / contentWidth;
    const zoomY = containerHeight / contentHeight;
    const optimalZoom = Math.min(zoomX, zoomY, 1);

    const centerX = (containerWidth - contentWidth * optimalZoom) / 2 - (minX - padding) * optimalZoom;
    const centerY = (containerHeight - contentHeight * optimalZoom) / 2 - (minY - padding) * optimalZoom;

    setZoom(optimalZoom);
    setPanOffset({ x: centerX, y: centerY });
  };

  const loadFloorplan = () => {
    loadFloorplanFromDatabase();
  };

  const loadFloorplanFromDatabase = async () => {
    try {
      const { data, error } = await supabase
        .from("floorplan_items")
        .select(
          `
          *,
          restaurant_tables!floorplan_items_table_id_fkey (*)
        `
        )
        .order("created_at");

      if (error) {
        console.error("Error fetching floorplan:", error);
        createInitialFloorplan();
        return;
      }

      if (data && data.length > 0) {
        const transformedItems: FloorplanItem[] = (data as any[]).map((item) => {
          if (item.type === "table" && item.restaurant_tables) {
            const hasOrder = (orders as any[]).some((order) => order.table_number === item.restaurant_tables.table_number);
            const actualStatus = hasOrder ? "occupied" : item.restaurant_tables.status;
            return {
              id: item.item_id,
              type: item.type as any,
              x: item.x,
              y: item.y,
              width: item.width,
              height: item.height,
              rotation: item.rotation,
              shape: item.shape as any,
              table: { ...item.restaurant_tables, status: actualStatus },
              label: item.label,
              color: item.color,
            };
          }
          return {
            id: item.item_id,
            type: item.type as any,
            x: item.x,
            y: item.y,
            width: item.width,
            height: item.height,
            rotation: item.rotation,
            shape: item.shape as any,
            table: item.restaurant_tables,
            label: item.label,
            color: item.color,
          };
        });

        setFloorplanItems(transformedItems);
      } else {
        createInitialFloorplan();
      }
    } catch (error) {
      console.error("Error loading floorplan:", error);
      createInitialFloorplan();
    }
  };

  const createInitialFloorplan = () => {
    const tableItems: FloorplanItem[] = (tables as any[]).map((table, index) => {
      const hasOrder = (orders as any[]).some((order) => order.table_number === table.table_number);
      const actualStatus = hasOrder ? "occupied" : table.status;
      return {
        id: `table-${table.id}`,
        type: "table",
        x: (index % 5) * 120 + 100,
        y: Math.floor(index / 5) * 120 + 150,
        width: 84,
        height: 84,
        rotation: 0,
        shape: "circle",
        table: { ...table, status: actualStatus },
      };
    });

    const roomItems: FloorplanItem[] = [
      {
        id: "entrance-1",
        type: "entrance",
        x: 60,
        y: 60,
        width: 140,
        height: 38,
        rotation: 0,
        label: "Entrance",
        color: "#10B981",
      },
      {
        id: "kitchen-1",
        type: "kitchen",
        x: 620,
        y: 60,
        width: 200,
        height: 140,
        rotation: 0,
        label: "Kitchen",
        color: "#EF4444",
      },
      {
        id: "bar-1",
        type: "bar",
        x: 320,
        y: 440,
        width: 240,
        height: 70,
        rotation: 0,
        label: "Bar",
        color: "#8B5CF6",
      },
      {
        id: "till-1",
        type: "till",
        x: 110,
        y: 440,
        width: 100,
        height: 70,
        rotation: 0,
        label: "Till",
        color: "#F59E0B",
      },
    ];

    setFloorplanItems([...tableItems, ...roomItems]);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPanning) return;

    const deltaX = e.clientX - panStart.x;
    const deltaY = e.clientY - panStart.y;

    setPanOffset((prev) => ({ x: prev.x + deltaX, y: prev.y + deltaY }));
    setPanStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (e.target === canvasRef.current || (e.target as HTMLElement).classList.contains("canvas-background")) {
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev * 1.2, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev / 1.2, 0.3));
  const resetView = () => autoFitFloorplan();

  const updateTableStatus = async (tableId: string, status: string) => {
    setFloorplanItems((items) =>
      items.map((item) =>
        item.table?.id === tableId && item.table ? { ...item, table: { ...item.table, status: status as any } } : item
      )
    );

    onTableStatusUpdate(tableId, status);
  };

  const handleTableClick = (item: FloorplanItem) => {
    if (item.type === "table" && item.table) onTableSelect(item.table);
  };

  const getTableColor = (status: string) => {
    switch (status) {
      case "available":
        return "#10B981";
      case "occupied":
        return "#EF4444";
      case "reserved":
        return "#F59E0B";
      case "cleaning":
        return "#6B7280";
      default:
        return "#6B7280";
    }
  };

  const getItemStyle = (item: FloorplanItem) => {
    const baseStyle: React.CSSProperties = {
      position: "absolute",
      left: item.x,
      top: item.y,
      width: item.width,
      height: item.height,
      transform: `rotate(${item.rotation}deg)`,
      transition: "box-shadow 0.15s ease, transform 0.15s ease, opacity 0.15s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      userSelect: "none",
      zIndex: 1,
    };

    if (item.type === "table" && item.table) {
      return {
        ...baseStyle,
        backgroundColor: "white",
        borderRadius: item.shape === "circle" ? "9999px" : "16px",
        border: "1px solid rgba(15, 23, 42, 0.15)",
        boxShadow: "0 8px 18px rgba(15, 23, 42, 0.10)",
        cursor: "pointer",
        color: "#0f172a",
      };
    }

    return {
      ...baseStyle,
      backgroundColor: item.color || "#64748b",
      borderRadius: "16px",
      border: "1px solid rgba(15, 23, 42, 0.15)",
      boxShadow: "0 8px 18px rgba(15, 23, 42, 0.10)",
      opacity: 0.95,
      cursor: "default",
      color: "white",
      padding: "0 10px",
      fontWeight: 800,
    };
  };

  return (
    <div className="flex flex-col h-full max-h-full overflow-hidden">
      {/* Header controls (light) */}
      <div className="flex items-start justify-between gap-4 mb-4 flex-shrink-0 px-2">
        <div>
          <div className="text-sm text-slate-500">Layout</div>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Restaurant Floorplan</h2>
          <p className="text-sm text-slate-500">Ctrl/⌘ + scroll to zoom • Drag to pan • Click tables to select</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Zoom controls */}
          <div className="flex items-center gap-2 rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm p-2">
            <button
              onClick={handleZoomOut}
              className="rounded-xl p-2 text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition"
              title="Zoom Out"
            >
              <ZoomOut size={18} />
            </button>

            <span className="text-sm font-extrabold text-slate-900 min-w-[4rem] text-center">
              {Math.round(zoom * 100)}%
            </span>

            <button
              onClick={handleZoomIn}
              className="rounded-xl p-2 text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition"
              title="Zoom In"
            >
              <ZoomIn size={18} />
            </button>

            <button
              onClick={resetView}
              className="rounded-xl p-2 text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition"
              title="Reset / Auto-fit"
            >
              <RotateCw size={18} />
            </button>
          </div>

          {/* Edit (manager) */}
          {currentStaff.role === "manager" && (
            <button
              onClick={onEditFloorplan}
              className="rounded-2xl px-4 py-3 bg-slate-900 text-white font-bold hover:bg-slate-800 active:bg-slate-950 transition shadow-sm"
            >
              <Edit size={18} className="mr-2 inline" />
              Edit Layout
            </button>
          )}
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 min-h-0 overflow-hidden px-2">
        <div
          ref={canvasRef}
          className="relative w-full h-full rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm overflow-hidden cursor-grab active:cursor-grabbing"
          style={{ minHeight: "420px" }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onMouseDown={handleCanvasMouseDown}
        >
          {/* Zoomable container */}
          <div
            className="absolute inset-0"
            style={{
              transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
              transformOrigin: "0 0",
              width: "2200px",
              height: "1600px",
            }}
          >
            {/* Grid background */}
            <div
              className="canvas-background absolute inset-0"
              style={{
                backgroundImage: `
                  linear-gradient(to right, rgba(15,23,42,0.06) 1px, transparent 1px),
                  linear-gradient(to bottom, rgba(15,23,42,0.06) 1px, transparent 1px)
                `,
                backgroundSize: "44px 44px",
              }}
            />

            {/* Items */}
            {floorplanItems.map((item) => {
              const isTable = item.type === "table" && item.table;
              const status = isTable ? (item.table as any).status : "";
              const accent = isTable ? getTableColor(status) : (item.color || "#64748b");

              return (
                <div
                  key={item.id}
                  style={getItemStyle(item)}
                  onClick={() => handleTableClick(item)}
                  onMouseDown={(e) => e.stopPropagation()}
                  className={cx(
                    "group",
                    isTable && "hover:shadow-lg",
                    isTable && "active:scale-[0.98]"
                  )}
                >
                  {isTable ? (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-1 px-2">
                      <div className="text-2xl font-extrabold leading-none">{(item.table as any).table_number}</div>

                      <div className={cx("inline-flex items-center gap-2 rounded-xl px-3 py-1 text-[11px] font-semibold ring-1", badgeForStatus(status))}>
                        <span className={cx("h-2 w-2 rounded-full", dotForStatus(status))} />
                        <span className="capitalize">{status}</span>
                      </div>

                      {/* subtle accent bar */}
                      <div
                        className="absolute bottom-0 left-0 right-0 h-1"
                        style={{ backgroundColor: accent, borderBottomLeftRadius: item.shape === "circle" ? 9999 : 16, borderBottomRightRadius: item.shape === "circle" ? 9999 : 16 }}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-sm font-extrabold">{item.label}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 mx-2 rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm p-3 flex-shrink-0">
        <div className="flex flex-wrap items-center justify-center gap-3">
          {["available", "occupied", "reserved", "cleaning"].map((s) => (
            <span
              key={s}
              className={cx("inline-flex items-center gap-2 rounded-xl px-3 py-1 text-xs font-semibold ring-1", badgeForStatus(s))}
            >
              <span className={cx("h-2 w-2 rounded-full", dotForStatus(s))} />
              <span className="capitalize">{s}</span>
            </span>
          ))}

          <span className="inline-flex items-center gap-2 rounded-xl px-3 py-1 text-xs font-semibold ring-1 ring-slate-200 bg-slate-50 text-slate-700">
            <Info size={14} className="text-slate-500" />
            Ctrl/⌘ + scroll to zoom • Drag to pan
          </span>
        </div>
      </div>
    </div>
  );
};

export default FloorplanView;
